import { icon } from '../../icons';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { authService } from '../../services/auth';
import { i18nService, onLocaleChange, t } from '../../services/i18n';
import { notificationsApi, type NotificationDto } from '../../services/notifications.api';

const NOTIFICATION_POLL_INTERVAL_MS = 60_000;
const MAX_VISIBLE_UNREAD_NOTIFICATIONS = 8;

export class RcNotificationMenu extends HTMLElement {
  private notifications: NotificationDto[] = [];
  private unreadCount = 0;
  private isOpen = false;
  private loading = false;
  private markAllBusy = false;
  private deleteAllBusy = false;
  private expandedUnread = false;
  private deletingNotificationIds = new Set<string>();
  private loadError = false;
  private pollHandle?: number;
  private disposeLocaleChange?: () => void;
  private handleDocumentClick = (event: MouseEvent) => {
    if (!this.contains(event.target as Node)) {
      this.closeMenu();
    }
  };

  connectedCallback(): void {
    authService.onAuthChange(() => {
      if (!authService.isAuthenticated) {
        this.notifications = [];
        this.unreadCount = 0;
        this.render();
        return;
      }

      void this.loadNotifications();
    });

    this.disposeLocaleChange = onLocaleChange(() => this.render());
    document.addEventListener('click', this.handleDocumentClick);
    this.pollHandle = window.setInterval(() => {
      if (authService.isAuthenticated) {
        void this.loadNotifications(false);
      }
    }, NOTIFICATION_POLL_INTERVAL_MS);

    this.render();
    if (authService.isAuthenticated) {
      void this.loadNotifications();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.pollHandle) {
      window.clearInterval(this.pollHandle);
    }
  }

  private async loadNotifications(renderLoading = true): Promise<void> {
    if (renderLoading) {
      this.loading = true;
      this.loadError = false;
      this.render();
    }

    try {
      const response = await notificationsApi.list();
      this.notifications = response.items;
      this.unreadCount = response.unreadCount;
      this.expandedUnread = this.expandedUnread && this.getHiddenUnreadCount() > 0;
      this.loadError = false;
    } catch (error) {
      console.error('Failed to load notifications:', error);
      this.loadError = true;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.currentUser) {
      this.innerHTML = '';
      return;
    }

    this.innerHTML = `
      <div class="notification-menu">
        <button
          type="button"
          class="notification-menu-trigger"
          aria-haspopup="true"
          aria-expanded="${this.isOpen}"
          aria-label="${this.escape(t('notifications.triggerAriaLabel'))}">
          <span class="notification-menu-icon">${icon('bell', { size: 18 })}</span>
          ${this.unreadCount > 0
            ? `<span class="notification-menu-badge">${this.unreadCount > 99 ? '99+' : this.unreadCount}</span>`
            : ''}
        </button>
        <div class="notification-menu-dropdown" ${this.isOpen ? '' : 'hidden'}>
          <div class="notification-menu-header">
            <div class="notification-menu-header-copy">
              <span class="notification-menu-title">${t('notifications.title')}</span>
              ${this.unreadCount > 0
                ? `<span class="notification-menu-count">${this.escape(t('notifications.unreadCount', { count: this.unreadCount }))}</span>`
                : ''}
            </div>
            <div class="notification-menu-header-actions">
              ${this.unreadCount > 0
                ? `<button
                    type="button"
                    class="notification-menu-action"
                    data-mark-all-read
                    ${this.markAllBusy ? 'disabled' : ''}>
                    ${this.escape(t('notifications.markAllRead'))}
                  </button>`
                : ''}
              ${this.notifications.length > 0
                ? `<button
                    type="button"
                    class="notification-menu-icon-action"
                    data-delete-all
                    aria-label="${this.escape(t('notifications.deleteAllAriaLabel'))}"
                    title="${this.escape(t('notifications.deleteAllAriaLabel'))}"
                    ${this.deleteAllBusy ? 'disabled' : ''}>
                    ${icon('trash', { size: 14 })}
                  </button>`
                : ''}
            </div>
          </div>
          ${this.renderBody()}
        </div>
      </div>
    `;

    this.querySelector('.notification-menu-trigger')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.isOpen = !this.isOpen;
      this.render();
    });

    this.querySelectorAll<HTMLButtonElement>('[data-notification-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const notificationId = button.dataset.notificationId;
        const targetUrl = button.dataset.targetUrl;
        if (!notificationId || !targetUrl) return;
        void this.handleNotificationClick(notificationId, targetUrl);
      });
    });

    this.querySelector<HTMLButtonElement>('[data-mark-all-read]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      void this.handleMarkAllRead();
    });

    this.querySelector<HTMLButtonElement>('[data-delete-all]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      void this.handleDeleteAll();
    });

    this.querySelector<HTMLButtonElement>('[data-toggle-hidden-unread]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.expandedUnread = !this.expandedUnread;
      this.render();
    });

    this.querySelectorAll<HTMLButtonElement>('[data-delete-notification-id]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const notificationId = button.dataset.deleteNotificationId;
        if (!notificationId) return;
        void this.handleDeleteNotification(notificationId);
      });
    });
  }

  private renderBody(): string {
    if (this.loading) {
      return `<div class="notification-menu-empty">${t('common.loading')}</div>`;
    }

    if (this.loadError) {
      return `<div class="notification-menu-empty">${t('notifications.loadError')}</div>`;
    }

    if (this.notifications.length === 0) {
      return `<div class="notification-menu-empty">${t('notifications.empty')}</div>`;
    }

    const unreadNotifications = this.notifications.filter((notification) => !notification.readAt);
    const readNotifications = this.notifications.filter((notification) => notification.readAt);
    const totalHiddenUnreadCount = Math.max(0, unreadNotifications.length - MAX_VISIBLE_UNREAD_NOTIFICATIONS);
    const visibleUnread = this.expandedUnread
      ? unreadNotifications
      : unreadNotifications.slice(0, MAX_VISIBLE_UNREAD_NOTIFICATIONS);

    return `
      <div class="notification-menu-list">
        ${visibleUnread.map((notification) => this.renderNotificationItem(notification)).join('')}
        ${totalHiddenUnreadCount > 0
          ? `<div class="notification-menu-overflow">
              <div class="notification-menu-overflow-copy">${this.escape(t('notifications.hiddenUnreadCount', { count: totalHiddenUnreadCount }))}</div>
              <button type="button" class="notification-menu-action" data-toggle-hidden-unread>
                ${this.escape(this.expandedUnread ? t('notifications.showFewerUnread') : t('notifications.showAllUnread'))}
              </button>
            </div>`
          : ''}
        ${readNotifications.length > 0
          ? `<div class="notification-menu-section-label">${this.escape(t('notifications.readSection'))}</div>
             ${readNotifications.map((notification) => this.renderNotificationItem(notification)).join('')}`
          : ''}
      </div>
    `;
  }

  private renderNotificationItem(notification: NotificationDto): string {
    const isUnread = !notification.readAt;
    const deleteBusy = this.deletingNotificationIds.has(notification.id);

    return `
      <div class="notification-menu-item${isUnread ? ' is-unread' : ''}">
        <button
          type="button"
          class="notification-menu-item-button"
          data-notification-id="${notification.id}"
          data-target-url="${this.escape(notification.targetUrl)}">
          <div class="notification-menu-item-title">${this.escape(this.getNotificationTitle(notification))}</div>
          <div class="notification-menu-item-message">${this.escape(this.getNotificationMessage(notification))}</div>
          <div class="notification-menu-item-meta">${this.escape(i18nService.formatDate(notification.createdAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}</div>
        </button>
        <button
          type="button"
          class="notification-menu-item-delete"
          data-delete-notification-id="${notification.id}"
          aria-label="${this.escape(t('notifications.deleteSingleAriaLabel'))}"
          title="${this.escape(t('notifications.deleteSingleAriaLabel'))}"
          ${deleteBusy ? 'disabled' : ''}>
          ${icon('trash', { size: 13 })}
        </button>
      </div>
    `;
  }

  private getNotificationTitle(notification: NotificationDto): string {
    switch (notification.type) {
      case 'tenant.application_assigned':
        return t('notifications.tenantApplicationAssigned.title');
      case 'tenant.agreement_assigned':
        return t('notifications.tenantAgreementAssigned.title');
      case 'tenant.agreement_updated':
        return t('notifications.tenantAgreementUpdated.title');
      case 'tenant.agreement_ending_soon':
        return t('notifications.tenantAgreementEndingSoon.title');
      case 'reminder.overdue':
        return t('notifications.reminderOverdue.title');
      case 'survey.response_submitted':
        return t('notifications.surveyResponse.title');
      case 'damage_report.created':
        return t('notifications.damageReportCreated.title');
      default:
        return t('notifications.generic.title');
    }
  }

  private getNotificationMessage(notification: NotificationDto): string {
    switch (notification.type) {
      case 'tenant.application_assigned':
        return t('notifications.tenantApplicationAssigned.message', {
          propertyAddress: notification.data.propertyAddress ?? t('common.property'),
        });
      case 'tenant.agreement_assigned':
        return t('notifications.tenantAgreementAssigned.message', {
          propertyAddress: notification.data.propertyAddress ?? t('common.property'),
        });
      case 'tenant.agreement_updated':
        return t('notifications.tenantAgreementUpdated.message', {
          propertyAddress: notification.data.propertyAddress ?? t('common.property'),
        });
      case 'tenant.agreement_ending_soon': {
        const endDate = notification.data.endDate
          ? i18nService.formatDate(notification.data.endDate, { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        return t('notifications.tenantAgreementEndingSoon.message', {
          propertyAddress: notification.data.propertyAddress ?? t('common.property'),
          endDate,
        });
      }
      case 'reminder.overdue': {
        const dueDate = notification.data.dueDate
          ? i18nService.formatDate(notification.data.dueDate, { day: 'numeric', month: 'short', year: 'numeric' })
          : '';
        return t('notifications.reminderOverdue.message', {
          title: notification.data.title ?? t('common.reminders'),
          dueDate,
        });
      }
      case 'survey.response_submitted':
        return t('notifications.surveyResponse.message', {
          tenantName: notification.data.tenantName ?? t('common.tenant'),
          year: notification.data.year ?? '',
        });
      case 'damage_report.created':
        return t('notifications.damageReportCreated.message', {
          tenantName: notification.data.tenantName ?? t('common.tenant'),
          propertyAddress: notification.data.propertyAddress ?? t('common.property'),
        });
      default:
        return t('notifications.generic.message');
    }
  }

  private async handleNotificationClick(notificationId: string, targetUrl: string): Promise<void> {
    const clickedNotification = this.notifications.find((notification) => notification.id === notificationId);

    if (clickedNotification && !clickedNotification.readAt) {
      clickedNotification.readAt = new Date().toISOString();
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.render();

      try {
        await notificationsApi.markRead(notificationId);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    window.location.href = targetUrl;
  }

  private async handleMarkAllRead(): Promise<void> {
    if (this.markAllBusy || this.unreadCount === 0) {
      return;
    }

    const previousNotifications = this.notifications.map((notification) => ({ ...notification }));
    const readAt = new Date().toISOString();

    this.markAllBusy = true;
    this.notifications = this.notifications.map((notification) =>
      notification.readAt ? notification : { ...notification, readAt });
    this.unreadCount = 0;
    this.render();

    try {
      await notificationsApi.markAllRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      this.notifications = previousNotifications;
      this.unreadCount = previousNotifications.filter((notification) => !notification.readAt).length;
    } finally {
      this.markAllBusy = false;
      this.render();
    }
  }

  private async handleDeleteNotification(notificationId: string): Promise<void> {
    if (this.deletingNotificationIds.has(notificationId)) {
      return;
    }

    const confirmed = await confirmDialog(t('notifications.confirmDeleteSingle'));
    if (!confirmed) {
      return;
    }

    const previousNotifications = this.notifications.map((notification) => ({ ...notification }));
    const deletedNotification = previousNotifications.find((notification) => notification.id === notificationId);

    this.deletingNotificationIds.add(notificationId);
    this.notifications = this.notifications.filter((notification) => notification.id !== notificationId);
    if (deletedNotification && !deletedNotification.readAt) {
      this.unreadCount = Math.max(0, this.unreadCount - 1);
    }
    if (this.getHiddenUnreadCount() === 0) {
      this.expandedUnread = false;
    }
    this.render();

    try {
      await notificationsApi.delete(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      this.notifications = previousNotifications;
      this.unreadCount = previousNotifications.filter((notification) => !notification.readAt).length;
    } finally {
      this.deletingNotificationIds.delete(notificationId);
      this.render();
    }
  }

  private async handleDeleteAll(): Promise<void> {
    if (this.deleteAllBusy || this.notifications.length === 0) {
      return;
    }

    const confirmed = await confirmDialog(t('notifications.confirmDeleteAll'));
    if (!confirmed) {
      return;
    }

    const previousNotifications = this.notifications.map((notification) => ({ ...notification }));
    this.deleteAllBusy = true;
    this.notifications = [];
    this.unreadCount = 0;
    this.expandedUnread = false;
    this.render();

    try {
      await notificationsApi.deleteAll();
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
      this.notifications = previousNotifications;
      this.unreadCount = previousNotifications.filter((notification) => !notification.readAt).length;
    } finally {
      this.deleteAllBusy = false;
      this.render();
    }
  }

  private getHiddenUnreadCount(): number {
    return Math.max(0, this.notifications.filter((notification) => !notification.readAt).length - MAX_VISIBLE_UNREAD_NOTIFICATIONS);
  }

  private closeMenu(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.render();
  }

  private escape(value: string): string {
    const span = document.createElement('span');
    span.textContent = value;
    return span.innerHTML;
  }
}

if (!customElements.get('rc-notification-menu')) {
  customElements.define('rc-notification-menu', RcNotificationMenu);
}
